import React, { useEffect, useRef } from 'react';
import './TransportationLogistics.css';

const TransportationLogistics = () => {
  const statsRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          // Animation logic
        }
      },
      { threshold: 0.3 }
    );

    if (statsRef.current) {
      observer.observe(statsRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const services = [
    {
      id: 1,
      icon: '🚗',
      title: 'Transportation & Vehicle Rental',
      description: 'Flexible transportation solutions tailored to government agencies, corporate institutions, diplomatic missions, NGOs, and individuals.',
      features: [
        'Executive chauffeur-driven vehicles',
        'Diplomatic and protocol transportation',
        'Corporate staff transportation',
        'Airport transfers and VIP logistics',
        'Event and conference transportation',
        'Short-term and long-term vehicle leasing',
        'Fleet management and outsourcing services'
      ],
      image: '/images/cars.png'
    },
    {
      id: 2,
      icon: '📋',
      title: 'Special Lease & Hire Purchase Programme',
      description: 'Flexible financing arrangements for organizations and qualified individuals to acquire vehicles, agricultural machinery, and equipment.',
      features: [
        'Vehicle lease-to-own solutions',
        'Corporate fleet acquisition',
        'Agricultural equipment financing',
        'Commercial vehicle financing',
        'Equipment leasing',
        'Flexible repayment structures',
        'Fleet and machinery renewal programmes'
      ],
      image: '/images/lease.png'
    },
    {
      id: 3,
      icon: '👨‍✈️',
      title: 'Drivers Empowerment Programme',
      description: 'Creating sustainable employment and improving professional driving standards across Africa.',
      features: [
        'Professional driver recruitment',
        'Driver training and certification',
        'Diplomatic and executive chauffeur services',
        'Corporate driver outsourcing',
        'Ride-hailing (app-based) driver support',
        'Vehicle financing opportunities for qualified drivers',
        'Road safety and customer service development'
      ],
      image: '/images/driver-empowerment.png'
    },
    {
      id: 4,
      icon: '🏗️',
      title: 'Heavy Marine, Construction & Agricultural Equipment Supply',
      description: 'High-quality heavy-duty machinery and specialized equipment for infrastructure, marine, construction, mining, and agriculture.',
      features: [
        'Excavators, Bulldozers, Wheel loaders',
        'Motor graders, Cranes, Forklifts',
        'Dump trucks, Marine vessels, Workboats',
        'Dredging equipment, Road construction equipment',
        'Agricultural tractors (2WD & 4WD)',
        'Combine harvesters, Ploughs, Harrows',
        'Seed planters, Boom sprayers, Irrigation equipment',
        'Rotavators, Tillers, Balers, Forage harvesters',
        'Trailers, Farm transport equipment',
        'Processing equipment & Agricultural implements'
      ],
      image: '/images/tractor.png'
    },
    {
      id: 5,
      icon: '🚛',
      title: 'Haulage, Cargo & Logistics Services',
      description: 'Integrated logistics and freight solutions ensuring efficient movement of goods and equipment throughout Nigeria and across Africa.',
      features: [
        'General haulage services',
        'Heavy cargo transportation',
        'Agricultural produce transportation',
        'Project logistics',
        'Container transportation',
        'Warehouse and distribution services',
        'Supply chain management',
        'Procurement logistics',
        'Last-mile delivery solutions',
        'Cross-border logistics support'
      ],
      image: '/images/cargo.png'
    },
    {
      id: 6,
      icon: '⛏️',
      title: 'Mining & Agricultural Support Services',
      description: 'Specialized logistics and operational support to the mining and agricultural sectors through strategic partnerships and industry expertise.',
      features: [
        'Equipment sourcing and supply',
        'Heavy equipment leasing',
        'Tractor and farm machinery leasing',
        'Mechanized farming support services',
        'Mining logistics and transportation',
        'Agricultural input distribution',
        'Fuel and lubricant supply',
        'Site mobilization services',
        'Materials handling',
        'Supply chain coordination',
        'Technical maintenance and after-sales support'
      ],
      image: '/images/mining.png'
    }
  ];

  const reasons = [
    {
      icon: '🔗',
      title: 'End-to-End Solutions',
      description: 'Comprehensive transportation and logistics solutions'
    },
    {
      icon: '👥',
      title: 'Professional Team',
      description: 'Professional and experienced personnel'
    },
    {
      icon: '🚗',
      title: 'Modern Fleet',
      description: 'Modern fleet and equipment'
    },
    {
      icon: '💰',
      title: 'Flexible Financing',
      description: 'Flexible leasing and financing options'
    },
    {
      icon: '🛡️',
      title: 'Safety & Excellence',
      description: 'Safety, reliability, and operational excellence'
    },
    {
      icon: '🌍',
      title: 'Pan-African Reach',
      description: 'Pan-African service delivery capability'
    }
  ];

  return (
    <div className="transport-page">
      {/* Hero Section - ABVIG Brand Colors */}
      <section 
        className="transport-hero"
        style={{
          backgroundImage: `linear-gradient(135deg, rgba(43, 15, 5, 0.92) 0%, rgba(74, 51, 38, 0.85) 50%, rgba(43, 15, 5, 0.92) 100%), url('https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=1600&h=900&fit=crop')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      >
        <div className="transport-hero-overlay"></div>
        <div className="transport-hero-content">
          <span className="transport-badge">ABVIG Logistics</span>
          <h1>Transportation & Logistics</h1>
          <p className="transport-subtitle">Driving Mobility, Logistics Excellence & Industrial Support Across Africa</p>
          <p className="transport-description">
            The ABVIG Transportation & Logistics Department provides comprehensive, reliable, and cost-effective 
            transportation, logistics, fleet management, equipment supply, and industrial support services.
          </p>
          <div className="transport-hero-stats" ref={statsRef}>
            <div className="stat-item">
              <span className="stat-number">6</span>
              <span className="stat-label">Core Services</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">50+</span>
              <span className="stat-label">Equipment Types</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">Pan-Africa</span>
              <span className="stat-label">Service Coverage</span>
            </div>
          </div>
          <button className="btn-transport" onClick={() => window.location.href = '/contact-us'}>
            Request a Quote
          </button>
        </div>
      </section>

      {/* Our Core Services - Instagram Style */}
      <section className="transport-services">
        <div className="container">
          <h2 className="section-title">Our Core Services</h2>
          <div className="services-grid">
            {services.map((service) => (
              <div className="service-card" key={service.id}>
                <div className="service-image-wrapper">
                  <div 
                    className="service-image" 
                    style={{backgroundImage: `url(${service.image})`}}
                  ></div>
                </div>
                <div className="service-content">
                  <div className="service-header">
                    <div className="service-icon">{service.icon}</div>
                    <h3>{service.title}</h3>
                  </div>
                  <p className="service-description">{service.description}</p>
                  <ul className="service-features">
                    {service.features.slice(0, 3).map((feature, idx) => (
                      <li key={idx}>✓ {feature}</li>
                    ))}
                    {service.features.length > 3 && (
                      <li className="more-features">+ {service.features.length - 3} more services</li>
                    )}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose ABVIG */}
      <section className="transport-why">
        <div className="container">
          <h2 className="section-title">Why Choose ABVIG Transportation & Logistics?</h2>
          <div className="why-grid">
            {reasons.map((reason, index) => (
              <div className="why-card" key={index}>
                <span className="why-icon">{reason.icon}</span>
                <h3>{reason.title}</h3>
                <p>{reason.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="transport-vision">
        <div className="container">
          <div className="vision-mission-grid">
            <div className="vision-card">
              <span className="vm-icon">👁️</span>
              <h3>Our Vision</h3>
              <p>
                To become Africa's leading provider of integrated transportation, logistics, fleet management, 
                agricultural mechanization, and industrial support services by delivering innovative, sustainable, 
                and customer-focused mobility solutions that drive economic development, food security, 
                and regional connectivity.
              </p>
            </div>
            <div className="mission-card">
              <span className="vm-icon">🎯</span>
              <h3>Our Mission</h3>
              <p>
                To provide safe, efficient, and world-class transportation, logistics, equipment supply, and 
                agricultural mechanization solutions through innovation, strategic partnerships, operational 
                excellence, and a commitment to supporting businesses, governments, farmers, and communities 
                across Africa.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="transport-cta">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Move?</h2>
            <p>
              Partner with ABVIG for reliable, efficient, and cost-effective transportation and logistics solutions.
            </p>
            <div className="cta-buttons">
              <button className="btn-transport-primary" onClick={() => window.location.href = '/contact-us'}>
                Contact Us Today
              </button>
              <button className="btn-transport-secondary" onClick={() => window.location.href = '/services'}>
                Explore All Services
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TransportationLogistics;